/**
 * FullAuditReport — renders the paid Full Audit v2
 * ("LinkedIn Profile and Positioning Audit") in 6 tabs:
 * Overview · Profile Rewrite · Discoverability · Proof and Credibility ·
 * LinkedIn Presence Plan · 30-Day Action Plan.
 *
 * Data source: analysis.full_audit_v2 (schema built in backend/full_audit_v2.py).
 * Legacy audits (no full_audit_v2) keep the old report layout.
 */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, AlertTriangle, Check, ChevronRight, ClipboardCopy, Info, Layers, Loader2, RefreshCw, ShieldCheck, TrendingUp, Target, Sparkles, Award, Users, Gauge, Compass, ChevronDown, MessageSquareQuote, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui/dropdown-menu';
import { AppShell } from '../Layout';
import { toast } from 'sonner';
import RebuildAuditCard from './RebuildAuditCard';

function CopyButton({ text, label = 'Copy', testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="secondary"
      size="sm"
      className="h-7 text-xs"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text || '');
          setCopied(true);
          toast.success('Copied to clipboard');
          setTimeout(() => setCopied(false), 1500);
        } catch { toast.error('Could not copy'); }
      }}
      data-testid={testid}
    >
      {copied ? <Check size={12} className="mr-1" /> : <ClipboardCopy size={12} className="mr-1" />}
      {label}
    </Button>
  );
}

const PRIORITY_TONES = {
  critical: 'bg-destructive/10 text-destructive border-destructive/30',
  high: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
  high_impact: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
  medium: 'bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/30',
  helpful: 'bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/30',
  optional: 'bg-secondary text-muted-foreground border-border',
};

function Pill({ value, testid }) {
  const tone = PRIORITY_TONES[(value || '').toLowerCase()] || PRIORITY_TONES.optional;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-mono-edge uppercase tracking-[0.12em] ${tone}`} data-testid={testid}>
      {(value || '').replace(/_/g, ' ')}
    </span>
  );
}

function SectionTitle({ children }) {
  return <h2 className="text-lg font-semibold mt-8 mb-3">{children}</h2>;
}

function EvidenceQuote({ children }) {
  if (!children) return null;
  return (
    <blockquote className="border-l-2 border-primary/50 pl-3 text-[13px] italic text-muted-foreground leading-snug">
      &ldquo;{children}&rdquo;
    </blockquote>
  );
}

// ---------------- Score hero (infographic) ----------------
// Circular SVG gauge + icon-badge grid + 3 at-a-glance tiles.
// Colors follow score bands: red < 50, amber 50-69, emerald 70-84, sapphire 85+.
function scoreBand(score) {
  if (score == null) return { label: 'Unscored', color: 'hsl(220 8% 55%)', ring: 'stroke-slate-400', bg: 'from-slate-500/20 to-slate-500/5', chip: 'text-slate-600 bg-slate-500/10' };
  if (score >= 85) return { label: 'Standout', color: 'rgb(56 189 248)', ring: 'stroke-sky-400', bg: 'from-sky-500/25 to-sky-500/5', chip: 'text-sky-700 bg-sky-500/10 dark:text-sky-300' };
  if (score >= 70) return { label: 'Strong', color: 'rgb(16 185 129)', ring: 'stroke-emerald-400', bg: 'from-emerald-500/25 to-emerald-500/5', chip: 'text-emerald-700 bg-emerald-500/10 dark:text-emerald-300' };
  if (score >= 50) return { label: 'Building', color: 'rgb(245 158 11)', ring: 'stroke-amber-400', bg: 'from-amber-500/25 to-amber-500/5', chip: 'text-amber-700 bg-amber-500/10 dark:text-amber-300' };
  return { label: 'Needs work', color: 'rgb(239 68 68)', ring: 'stroke-rose-400', bg: 'from-rose-500/25 to-rose-500/5', chip: 'text-rose-700 bg-rose-500/10 dark:text-rose-300' };
}

function ScoreGauge({ score, band }) {
  const size = 168;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score || 0));
  const offset = c - (pct / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="fa2-score-gauge">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} className="stroke-secondary" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke}
          fill="none" strokeLinecap="round"
          stroke={band.color}
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(.6,.1,.2,1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-bold tabular-nums leading-none" data-testid="fa2-overall-score">{score != null ? score : '—'}</span>
        <span className="text-[11px] text-muted-foreground mt-0.5">/ 100</span>
        <span className={`mt-1.5 inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-mono-edge uppercase tracking-[0.14em] ${band.chip}`}>{band.label}</span>
      </div>
    </div>
  );
}

function InfoBadge({ icon: Icon, label, value, sub, testid }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-border bg-card/70 px-3 py-2.5" data-testid={testid}>
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
        <Icon size={14} />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-mono-edge tracking-[0.16em] uppercase text-muted-foreground">{label}</p>
        <p className="text-[13px] font-semibold leading-tight mt-0.5 truncate" title={value}>{value}</p>
        {sub && <p className="text-[10.5px] text-muted-foreground mt-0.5 truncate" title={sub}>{sub}</p>}
      </div>
    </div>
  );
}

function GlanceTile({ tone, icon: Icon, label, text, testid }) {
  const tones = {
    emerald: 'border-emerald-500/30 bg-emerald-500/[0.05] text-emerald-700 dark:text-emerald-300',
    amber: 'border-amber-500/30 bg-amber-500/[0.05] text-amber-700 dark:text-amber-300',
    sky: 'border-sky-500/30 bg-sky-500/[0.05] text-sky-700 dark:text-sky-300',
  };
  if (!text) return null;
  return (
    <div className={`rounded-xl border p-3.5 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10.5px] font-mono-edge tracking-[0.16em] uppercase">
        <Icon size={12} /> {label}
      </div>
      <p className="mt-1.5 text-[13px] leading-snug text-foreground line-clamp-3">{text}</p>
    </div>
  );
}

const SLIDER_OPTIONS = [
  { key: 'strict', label: 'Strict', help: 'Fact-first, declarative — metric leads every line.' },
  { key: 'balanced', label: 'Balanced', help: 'Light context before the result — plain language.' },
  { key: 'high_storytelling', label: 'High-storytelling', help: 'Setup → tension → turn → result.' },
];

function labelForSlider(key) {
  return SLIDER_OPTIONS.find((o) => o.key === key)?.label || 'Balanced';
}

// ---------------- Premium Overview redesign helpers ----------------
// The 4 headline dimensions surfaced on the Summary card. All other
// dimensions still appear in the collapsible scorecard below.
const HEADLINE_DIM_KEYS = [
  'positioning_clarity',
  'goal_audience_alignment',
  'search_discoverability',
  'credibility_proof',
];

// Short verdict per score band, used in the collapsed scorecard row so
// the user gets a signal without reading a paragraph.
function scoreVerdict(score) {
  if (score == null) return { label: 'Not scored', tone: 'muted' };
  if (score >= 85) return { label: 'Standout', tone: 'sky' };
  if (score >= 75) return { label: 'Strong', tone: 'emerald' };
  if (score >= 65) return { label: 'Solid, refine', tone: 'emerald' };
  if (score >= 50) return { label: 'Needs improvement', tone: 'amber' };
  return { label: 'Priority fix', tone: 'red' };
}

// One-liner impact label per priority — used on the Fix These First cards.
const IMPACT_LABEL = {
  critical: { label: 'Critical fix', dot: 'bg-red-600', text: 'text-red-700 dark:text-red-400' },
  high: { label: 'High impact', dot: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
  medium: { label: 'Medium impact', dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  low: { label: 'Nice-to-have', dot: 'bg-sky-500', text: 'text-sky-600 dark:text-sky-400' },
};

// Rough "minutes" heuristic per priority so the Fix Card feels actionable.
const IMPACT_MINUTES = { critical: '5-15 min fix', high: '5-15 min fix', medium: '10-20 min fix', low: '5 min fix' };

// Estimated score uplift if the top gaps are fixed (used on the sticky bar).
// Purely presentational — capped at 15 so we never overpromise.
function estimateUplift(gaps) {
  const count = (p) => gaps.filter((g) => String(g.priority).toLowerCase() === p).length;
  return Math.min(15, count('critical') * 4 + count('high') * 3 + count('medium') * 2);
}

// Semantic tone → tailwind class helpers (keeps the palette single-source).
const TONE_TEXT = {
  emerald: 'text-emerald-700 dark:text-emerald-300',
  amber: 'text-amber-700 dark:text-amber-300',
  red: 'text-red-700 dark:text-red-400',
  sky: 'text-sky-700 dark:text-sky-300',
  muted: 'text-muted-foreground',
};
const TONE_DOT = {
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  sky: 'bg-sky-500',
  muted: 'bg-slate-400',
};
const TONE_BAR = {
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  sky: 'bg-sky-500',
  muted: 'bg-slate-400',
};

// ---------------- Premium Summary card ----------------
function PremiumSummaryCard({ audit, onGoToRewrite, onChangeGoal, onRegenerateTone, regenerating }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const band = scoreBand(audit.overallScore);
  const s = audit.auditSummary || {};
  const dims = audit.dimensions || [];
  const headline = HEADLINE_DIM_KEYS
    .map((k) => dims.find((d) => d.key === k))
    .filter(Boolean);

  const overall = audit.overallScore;
  const verdict = scoreVerdict(overall);
  const scoreColor = verdict.tone === 'sky' ? 'text-sky-600 dark:text-sky-400'
    : verdict.tone === 'emerald' ? 'text-emerald-600 dark:text-emerald-400'
    : verdict.tone === 'amber' ? 'text-amber-600 dark:text-amber-400'
    : verdict.tone === 'red' ? 'text-red-600 dark:text-red-500'
    : 'text-slate-600';

  return (
    <Card
      className="border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm rounded-2xl overflow-hidden"
      data-testid="fa2-summary-card"
    >
      <CardContent className="p-6 sm:p-8">
        <div className="grid gap-8 md:grid-cols-[auto_1fr] md:items-start">
          {/* Score block — clean number-forward */}
          <div className="flex flex-col items-start md:items-center gap-1.5 md:min-w-[168px]">
            <p className="text-[10.5px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">
              Profile strength
            </p>
            <div className="flex items-baseline gap-1.5" data-testid="fa2-summary-score">
              <span className={`text-6xl font-bold tabular-nums leading-none ${scoreColor}`}>
                {overall != null ? overall : '—'}
              </span>
              <span className="text-xl font-semibold text-muted-foreground">/100</span>
            </div>
            <div className={`mt-1 inline-flex items-center gap-1.5 text-[13px] font-semibold ${scoreColor}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${TONE_DOT[verdict.tone]}`} />
              {audit.scoreLabel || `${band.label} foundation`}
            </div>
          </div>

          {/* Right column — verdict + top opportunity + CTAs */}
          <div className="min-w-0">
            {s.currentPositioning && (
              <p className="text-[15px] leading-relaxed text-slate-700 dark:text-slate-200 max-w-[68ch]">
                {s.currentPositioning}
              </p>
            )}

            {(s.priorityImprovement || s.recommendedPositioning) && (
              <div className="mt-4 rounded-xl border border-primary/20 bg-primary/[0.04] p-4">
                <p className="text-[10.5px] font-mono-edge tracking-[0.16em] uppercase text-primary flex items-center gap-1.5">
                  <Sparkles size={12} /> Top opportunity
                </p>
                <p className="mt-1 text-[14.5px] leading-relaxed text-slate-800 dark:text-slate-100 max-w-[68ch]">
                  {s.priorityImprovement || s.recommendedPositioning}
                </p>
              </div>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-2.5" data-testid="fa2-summary-ctas">
              <Button
                size="sm"
                onClick={onGoToRewrite}
                className="rounded-full h-9 px-5 shadow-sm"
                data-testid="fa2-summary-improve-btn"
              >
                Improve my profile <ArrowRight size={14} className="ml-1.5" />
              </Button>
              {onChangeGoal && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onChangeGoal}
                  className="rounded-full h-9 px-4"
                  data-testid="fa2-summary-change-goal-btn"
                >
                  Change goal
                </Button>
              )}
              {audit.languageSlider && onRegenerateTone && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      disabled={regenerating}
                      className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[13px] text-muted-foreground hover:text-primary hover:bg-primary/[0.04] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                      data-testid="fa2-summary-tone-trigger"
                      title="Regenerate the rewrites in a different tone"
                    >
                      {regenerating
                        ? <><Loader2 size={13} className="animate-spin" /> Rewriting…</>
                        : <><RefreshCw size={13} /> Tone: {labelForSlider(audit.languageSlider)}</>}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-64" data-testid="fa2-summary-tone-menu">
                    <p className="px-2.5 pt-2 pb-1 text-[10.5px] font-mono-edge tracking-[0.16em] uppercase text-muted-foreground">
                      Regenerate under…
                    </p>
                    {SLIDER_OPTIONS.map((opt) => {
                      const isCurrent = audit.languageSlider === opt.key;
                      return (
                        <DropdownMenuItem
                          key={opt.key}
                          disabled={isCurrent || regenerating}
                          onSelect={(e) => {
                            e.preventDefault();
                            if (!isCurrent) onRegenerateTone(opt.key);
                          }}
                          className="flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                          data-testid={`fa2-summary-tone-${opt.key}`}
                        >
                          <div className="flex w-full items-center justify-between">
                            <span className="text-[13px] font-semibold">{opt.label}</span>
                            {isCurrent && (
                              <span className="text-[10px] font-mono-edge tracking-[0.16em] uppercase text-muted-foreground">Current</span>
                            )}
                          </div>
                          <span className="text-[11.5px] text-muted-foreground leading-snug">{opt.help}</span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>

        {/* 4 compact dimension indicators — the only "at-a-glance" grid */}
        {headline.length > 0 && (
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="fa2-summary-dims">
            {headline.map((d) => {
              const v = scoreVerdict(d.score);
              return (
                <div
                  key={d.key}
                  className="rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 px-3.5 py-3"
                  data-testid={`fa2-summary-dim-${d.key}`}
                >
                  <p className="text-[10.5px] font-mono-edge tracking-[0.14em] uppercase text-muted-foreground truncate" title={d.name}>
                    {d.name}
                  </p>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className={`text-2xl font-bold tabular-nums leading-none ${TONE_TEXT[v.tone]}`}>
                      {d.score != null ? d.score : '—'}
                    </span>
                  </div>
                  <div className="mt-2 h-1 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div
                      className={`h-full ${TONE_BAR[v.tone]}`}
                      style={{ width: `${Math.max(2, Math.min(100, d.score || 0))}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Audit details drawer — pushes seniority / coverage / confidence / target
             out of the hero and into a progressive disclosure. */}
        <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen} className="mt-6">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground hover:text-primary transition-colors"
              data-testid="fa2-summary-details-toggle"
            >
              <ChevronDown size={13} className={`transition-transform ${detailsOpen ? 'rotate-180' : ''}`} />
              {detailsOpen ? 'Hide' : 'Show'} audit details
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="fa2-summary-details">
            <InfoBadge icon={Users} label="Career stage" value={audit.careerStage ? audit.careerStage.charAt(0).toUpperCase() + audit.careerStage.slice(1) : null} />
            <InfoBadge icon={TrendingUp} label="Seniority" value={audit.detectedSeniority} />
            <InfoBadge icon={Compass} label="Objective" value={audit.selectedObjectiveLabel} />
            <InfoBadge icon={Gauge} label="Data coverage" value={audit.dataCoverage != null ? `${audit.dataCoverage}%` : null} sub={audit.dataCoverageLevel} />
            <InfoBadge icon={ShieldCheck} label="Confidence" value={audit.auditConfidence ? audit.auditConfidence.charAt(0).toUpperCase() + audit.auditConfidence.slice(1) : null} />
            {audit.targetAudienceOrOpportunity && (
              <InfoBadge icon={Target} label="Target" value={audit.targetAudienceOrOpportunity} />
            )}
            {audit.confidenceReason && (
              <p className="col-span-full text-[11.5px] text-muted-foreground flex items-start gap-1.5" data-testid="fa2-confidence-reason">
                <Info size={11} className="mt-0.5 shrink-0" /> {audit.confidenceReason}
              </p>
            )}
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}

// ---------------- "Fix these first" — top 3 priority improvements ----------------
function FixTheseFirst({ gaps, onGoToRewrite }) {
  const top = (gaps || []).slice(0, 3);
  if (top.length === 0) return null;
  return (
    <div className="mt-8" data-testid="fa2-fix-first">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <p className="text-[10.5px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">Priority</p>
          <h2 className="mt-1 text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-50">
            Your {top.length} highest-impact improvements
          </h2>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {top.map((g, i) => {
          const p = String(g.priority || 'medium').toLowerCase();
          const impact = IMPACT_LABEL[p] || IMPACT_LABEL.medium;
          const minutes = IMPACT_MINUTES[p] || IMPACT_MINUTES.medium;
          return (
            <Card
              key={i}
              className="border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 rounded-2xl shadow-sm hover:shadow-md transition-shadow"
              data-testid={`fa2-fix-card-${i}`}
            >
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 flex flex-col items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                    {i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] sm:text-[16px] font-semibold text-slate-900 dark:text-slate-50 leading-snug">
                      {g.section}
                    </p>
                    {g.diagnosis && (
                      <p className="mt-1.5 text-[14px] leading-relaxed text-slate-600 dark:text-slate-300 max-w-[70ch]">
                        {g.diagnosis}
                      </p>
                    )}
                    {g.recommendedAction && (
                      <p className="mt-2.5 text-[14px] leading-relaxed text-slate-800 dark:text-slate-100 max-w-[70ch]">
                        <span className={`font-semibold ${impact.text}`}>Recommended: </span>
                        {g.recommendedAction}
                      </p>
                    )}
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <span className={`inline-flex items-center gap-1.5 text-[12.5px] font-medium ${impact.text}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${impact.dot}`} /> {impact.label}
                      </span>
                      <span className="text-[12.5px] text-muted-foreground">{minutes}</span>
                      <div className="ml-auto">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={onGoToRewrite}
                          className="h-8 rounded-full text-primary hover:bg-primary/[0.06] hover:text-primary px-3"
                          data-testid={`fa2-fix-card-cta-${i}`}
                        >
                          See recommended rewrite <ChevronRight size={13} className="ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- Score overview — horizontal bars, sorted by priority ----------------
function ScoreOverview({ dimensions }) {
  const sorted = useMemo(() => {
    // Sort ascending by score so the biggest gaps rise to the top —
    // matches the user's spec: "sort by improvement priority".
    return [...(dimensions || [])].sort((a, b) => (a.score ?? 0) - (b.score ?? 0));
  }, [dimensions]);
  if (sorted.length === 0) return null;
  return (
    <div className="mt-10" data-testid="fa2-score-overview">
      <p className="text-[10.5px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">Overview</p>
      <h2 className="mt-1 text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-50">
        Score across all profile areas
      </h2>
      <Card className="mt-4 border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 rounded-2xl shadow-sm">
        <CardContent className="p-5 sm:p-6">
          <ul className="divide-y divide-slate-200/70 dark:divide-slate-800">
            {sorted.map((d) => {
              const v = scoreVerdict(d.score);
              return (
                <li key={d.key} className="py-3 first:pt-0 last:pb-0" data-testid={`fa2-overview-row-${d.key}`}>
                  <div className="flex items-center gap-3">
                    <p className="text-[14px] text-slate-800 dark:text-slate-100 flex-1 min-w-0 truncate">{d.name}</p>
                    <span className={`shrink-0 text-[12px] font-medium ${TONE_TEXT[v.tone]}`}>{v.label}</span>
                    <span className={`shrink-0 tabular-nums text-[14px] font-semibold w-10 text-right ${TONE_TEXT[v.tone]}`}>
                      {d.score != null ? d.score : '—'}
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div
                      className={`h-full transition-all ${TONE_BAR[v.tone]}`}
                      style={{ width: `${Math.max(2, Math.min(100, d.score || 0))}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------- Collapsible scorecard — one row per dimension ----------------
function CollapsibleScorecardRow({ d, onRegenerateDimension, regeneratingDimKey }) {
  const [open, setOpen] = useState(false);
  const v = scoreVerdict(d.score);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border-b border-slate-200/70 dark:border-slate-800 last:border-b-0">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center gap-3 py-4 px-1 text-left hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors rounded-lg"
          data-testid={`fa2-scorecard-row-${d.key}`}
        >
          <span className={`shrink-0 tabular-nums text-[15px] font-bold w-10 ${TONE_TEXT[v.tone]}`}>
            {d.score != null ? d.score : '—'}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[14.5px] font-semibold text-slate-900 dark:text-slate-50 truncate">{d.name}</p>
            <p className={`text-[12.5px] mt-0.5 ${TONE_TEXT[v.tone]}`}>
              {v.label}
              {d.recommendation && <span className="text-muted-foreground"> · 1 improvement</span>}
            </p>
          </div>
          <span className="shrink-0 text-[12.5px] text-primary inline-flex items-center gap-1">
            {open ? 'Hide' : 'View'} details
            <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pb-5 pl-13 pr-1 space-y-3 text-[14px] leading-relaxed" data-testid={`fa2-scorecard-detail-${d.key}`}>
        {d.strength && (
          <div className="rounded-lg border border-emerald-200/70 dark:border-emerald-900/50 bg-emerald-50/70 dark:bg-emerald-950/20 p-3">
            <p className="text-[11px] font-mono-edge tracking-[0.14em] uppercase text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
              <Check size={11} /> What&rsquo;s working
            </p>
            <p className="mt-1 text-slate-800 dark:text-slate-100">{d.strength}</p>
          </div>
        )}
        {d.limitation && (
          <div className="rounded-lg border border-amber-200/70 dark:border-amber-900/50 bg-amber-50/70 dark:bg-amber-950/20 p-3">
            <p className="text-[11px] font-mono-edge tracking-[0.14em] uppercase text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
              <AlertTriangle size={11} /> What&rsquo;s limiting your profile
            </p>
            <p className="mt-1 text-slate-800 dark:text-slate-100">{d.limitation}</p>
          </div>
        )}
        {d.recommendation && (
          <div className="rounded-lg border border-primary/25 bg-primary/[0.04] p-3">
            <p className="text-[11px] font-mono-edge tracking-[0.14em] uppercase text-primary flex items-center gap-1.5">
              <Sparkles size={11} /> Recommended change
            </p>
            <p className="mt-1 text-slate-800 dark:text-slate-100">{d.recommendation}</p>
          </div>
        )}
        {(d.evidence || []).length > 0 && (
          <Collapsible>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-[12.5px] text-muted-foreground hover:text-primary transition-colors"
                data-testid={`fa2-scorecard-evidence-toggle-${d.key}`}
              >
                <ChevronDown size={12} /> Why this recommendation?
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-1.5">
              {d.evidence.slice(0, 3).map((e, i) => <EvidenceQuote key={i}>{e}</EvidenceQuote>)}
            </CollapsibleContent>
          </Collapsible>
        )}
        <div className="flex items-center gap-3 text-[11.5px] text-muted-foreground pt-1">
          <span>Weight {d.weight}%</span>
          <span>Contribution +{d.weightedContribution}</span>
          {onRegenerateDimension && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRegenerateDimension(d.key); }}
              disabled={regeneratingDimKey === d.key}
              className="ml-auto inline-flex items-center gap-1 rounded-full border border-slate-200 dark:border-slate-700 hover:border-primary/50 hover:text-primary hover:bg-primary/[0.04] px-2.5 py-0.5 text-[11px] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              title="Regenerate just this dimension's diagnosis"
              data-testid={`fa2-scorecard-regen-${d.key}`}
            >
              {regeneratingDimKey === d.key ? (
                <><Loader2 size={10} className="animate-spin" /> Rewriting…</>
              ) : (
                <><RefreshCw size={10} /> Regenerate</>
              )}
            </button>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function CollapsibleScorecard({ dimensions, onRegenerateDimension, regeneratingDimKey, scoreExplanation }) {
  const [showMath, setShowMath] = useState(false);
  if (!dimensions || dimensions.length === 0) return null;
  return (
    <div className="mt-10" data-testid="fa2-scorecard">
      <p className="text-[10.5px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">Detailed audit</p>
      <h2 className="mt-1 text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-50">
        Detailed scorecard
      </h2>
      <Card className="mt-4 border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 rounded-2xl shadow-sm">
        <CardContent className="p-5 sm:p-6">
          {dimensions.map((d) => (
            <CollapsibleScorecardRow
              key={d.key}
              d={d}
              onRegenerateDimension={onRegenerateDimension}
              regeneratingDimKey={regeneratingDimKey}
            />
          ))}
        </CardContent>
      </Card>
      {scoreExplanation && (
        <>
          <button
            type="button"
            onClick={() => setShowMath(!showMath)}
            className="mt-3 text-[12.5px] text-primary underline underline-offset-2"
            data-testid="fa2-score-math-toggle"
          >
            {showMath ? 'Hide' : 'How this score was calculated'}
          </button>
          {showMath && (
            <p className="mt-2 text-[12.5px] text-muted-foreground max-w-2xl leading-relaxed" data-testid="fa2-score-math">
              {scoreExplanation}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ---------------- Strongest signals — kept but toned down for calm hierarchy ----------------
function StrongestSignals({ signals }) {
  const list = signals || [];
  if (list.length === 0) return null;
  return (
    <div className="mt-10" data-testid="fa2-signals">
      <p className="text-[10.5px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">Strengths</p>
      <h2 className="mt-1 text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-50">
        What&rsquo;s already working for you
      </h2>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {list.map((sig, i) => (
          <Card
            key={i}
            className="border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 rounded-2xl shadow-sm"
          >
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 shrink-0 rounded-full bg-emerald-500/12 text-emerald-600 dark:text-emerald-400 p-1.5">
                  <Check size={13} />
                </span>
                <div className="min-w-0 space-y-1.5">
                  <p className="text-[14.5px] font-semibold text-slate-900 dark:text-slate-50 leading-snug">{sig.signal}</p>
                  {sig.whyValuable && (
                    <p className="text-[13.5px] leading-relaxed text-slate-600 dark:text-slate-300">
                      {sig.whyValuable}
                    </p>
                  )}
                  {sig.howToAmplify && (
                    <p className="text-[13.5px] leading-relaxed text-slate-800 dark:text-slate-100">
                      <span className="font-semibold text-primary">Amplify: </span>{sig.howToAmplify}
                    </p>
                  )}
                  {sig.evidence && (
                    <Collapsible>
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-primary transition-colors"
                        >
                          <ChevronDown size={11} /> Evidence
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-1.5">
                        <EvidenceQuote>{sig.evidence}</EvidenceQuote>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------------- Sticky next-step bar ----------------
function StickyImprovementBar({ audit, onGoToRewrite }) {
  const gaps = audit.priorityGaps || [];
  const sections = gaps.length;
  const uplift = estimateUplift(gaps);
  const current = audit.overallScore;
  const target = current != null ? Math.min(100, current + uplift) : null;
  if (sections === 0) return null;
  return (
    <div
      className="sticky bottom-4 z-30 mt-8"
      data-testid="fa2-sticky-bar"
    >
      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white/95 dark:bg-slate-950/95 backdrop-blur-md shadow-lg shadow-slate-900/[0.06] px-4 py-3 sm:px-5 sm:py-4">
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-[14.5px] font-semibold text-slate-900 dark:text-slate-50 leading-tight">
              Ready to improve your profile?
            </p>
            <p className="mt-0.5 text-[12.5px] text-muted-foreground leading-snug">
              {sections} section{sections === 1 ? '' : 's'} need attention
              {target != null && current != null && uplift > 0 && (
                <>
                  <span className="mx-1.5">·</span>
                  Estimated improvement:{' '}
                  <span className="font-semibold text-slate-800 dark:text-slate-100 tabular-nums">{current}</span>
                  <ArrowRight size={11} className="inline mx-1 -mt-0.5" />
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">{target}</span>
                </>
              )}
            </p>
          </div>
          <Button
            size="sm"
            onClick={onGoToRewrite}
            className="shrink-0 rounded-full h-9 px-5 shadow-sm"
            data-testid="fa2-sticky-bar-cta"
          >
            Rewrite my profile <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Overview tab (redesigned, premium) ----------------
export function OverviewTab({ audit, rawData, onRegenerateTone, regenerating, onRegenerateDimension, regeneratingDimKey, onGoToRewrite, onChangeGoal }) {
  return (
    <div data-testid="fa2-overview-tab">
      {audit.stageConflict && (
        <div
          className="mb-4 flex items-start gap-2.5 rounded-xl border border-amber-300/70 bg-amber-50/80 dark:border-amber-900/40 dark:bg-amber-950/20 px-3.5 py-2.5 text-[13.5px] text-amber-900 dark:text-amber-200"
          data-testid="fa2-stage-conflict"
        >
          <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {audit.stageConflict}
        </div>
      )}
      {audit.insufficientDataMessage && (
        <div
          className="mb-4 rounded-xl border border-destructive/40 bg-destructive/[0.06] px-3.5 py-2.5 text-[13.5px]"
          data-testid="fa2-insufficient-message"
        >
          {audit.insufficientDataMessage}
        </div>
      )}

      {/* 1. Premium summary card */}
      <PremiumSummaryCard
        audit={audit}
        onGoToRewrite={onGoToRewrite}
        onChangeGoal={onChangeGoal}
        onRegenerateTone={onRegenerateTone}
        regenerating={regenerating}
      />

      {/* 1b. Rebuild-audit banner — surfaces immediately below the pie
          charts when the original rewrite / v2 sections timed out. Amber
          glow + ping dot make it hard to miss. Renders null when not
          needed, so cost is zero on healthy audits. */}
      <RebuildAuditCard data={rawData} />

      {/* 2. Fix these first — top 3 priority improvements */}
      <FixTheseFirst gaps={audit.priorityGaps || []} onGoToRewrite={onGoToRewrite} />

      {/* 3. Score overview — horizontal bars, sorted by priority */}
      <ScoreOverview dimensions={audit.dimensions || []} />

      {/* 4. Collapsible detailed scorecard */}
      <CollapsibleScorecard
        dimensions={audit.dimensions || []}
        onRegenerateDimension={onRegenerateDimension}
        regeneratingDimKey={regeneratingDimKey}
        scoreExplanation={audit.scoreExplanation}
      />

      {/* 5. Strongest signals — recognition, calm hierarchy */}
      <StrongestSignals signals={audit.strongestSignals || []} />

      {(audit.assumptions || []).length > 0 && (
        <div className="mt-10" data-testid="fa2-assumptions">
          <p className="text-[10.5px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">Context</p>
          <h2 className="mt-1 text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-50">
            Assumptions we made
          </h2>
          <ul className="mt-3 list-disc pl-5 space-y-1 text-[13.5px] text-slate-600 dark:text-slate-300 leading-relaxed">
            {audit.assumptions.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}

      {/* 6. Sticky improvement bar */}
      <StickyImprovementBar audit={audit} onGoToRewrite={onGoToRewrite} />
    </div>
  );
}

// ---------------- Profile Rewrite tab ----------------
// Section themes give each rewrite block a subtle colour identity via a
// left accent bar + muted chip only. Cards themselves stay clean white
// so the palette reads premium, not pastel. Muted "current" tiles vs
// vibrant "rewrite" tiles reinforce the contrast.
const REWRITE_THEMES = {
  headline: {
    bar: 'bg-gradient-to-b from-sky-400 to-blue-500',
    chip: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  },
  banner: {
    bar: 'bg-gradient-to-b from-violet-400 to-purple-500',
    chip: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  },
  about: {
    bar: 'bg-gradient-to-b from-emerald-400 to-teal-500',
    chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  },
  experience: {
    bar: 'bg-gradient-to-b from-amber-400 to-orange-500',
    chip: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  },
  education: {
    bar: 'bg-gradient-to-b from-cyan-400 to-sky-500',
    chip: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
  },
  skills: {
    bar: 'bg-gradient-to-b from-rose-400 to-pink-500',
    chip: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
  },
  ba: {
    bar: 'bg-gradient-to-b from-indigo-400 to-violet-500',
    chip: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300',
  },
};

function RewriteSection({ theme, eyebrow, title, subtitle, children, testid }) {
  const t = REWRITE_THEMES[theme] || REWRITE_THEMES.headline;
  return (
    <section
      className="relative mt-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm p-5 sm:p-6 overflow-hidden"
      data-testid={testid}
    >
      <span className={`absolute left-0 top-0 h-full w-1 ${t.bar}`} aria-hidden="true" />
      <div className="pl-3 sm:pl-4">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10.5px] font-mono-edge uppercase tracking-[0.16em] ${t.chip}`}>{eyebrow}</span>
        </div>
        <h2 className="text-lg sm:text-xl font-semibold leading-tight text-slate-900 dark:text-slate-50">{title}</h2>
        {subtitle && <p className="text-[13.5px] text-muted-foreground mt-1 leading-relaxed max-w-[70ch]">{subtitle}</p>}
        <div className="mt-4">{children}</div>
      </div>
    </section>
  );
}

function CurrentBlock({ label = 'Current on LinkedIn', children, testid }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-secondary/50 p-3.5" data-testid={testid}>
      <p className="text-[10px] font-mono-edge tracking-[0.18em] uppercase text-muted-foreground mb-1.5">{label}</p>
      <div className="text-[13px] text-muted-foreground italic leading-relaxed">{children}</div>
    </div>
  );
}

function CompareTonesPanel({
  currentSlider, currentHeadline, currentAbout, currentExperienceRewrites,
  compareSlider, setCompareSlider, toneVariants, archivedSliders,
  onRegenerateTone, regenerating, headlineCharLimit,
}) {
  const compareVariant = compareSlider ? toneVariants[compareSlider] : null;
  const allSliders = ['strict', 'balanced', 'high_storytelling'];
  const availableCompareOptions = allSliders.filter((s) => s !== currentSlider);
  return (
    <Card className="border-primary/30 bg-primary/[0.03] my-4" data-testid="fa2-compare-panel">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="min-w-0">
            <p className="text-[10.5px] font-mono-edge tracking-[0.22em] uppercase text-primary flex items-center gap-1.5">
              <Layers size={11} /> Compare rewrite tones
            </p>
            <p className="text-[12.5px] text-muted-foreground mt-0.5">
              Pin any second tone alongside your current one and pick the lines that fit your voice best.
            </p>
          </div>
          <div className="flex items-center gap-1.5" data-testid="fa2-compare-picker">
            <span className="text-[11px] text-muted-foreground uppercase tracking-[0.14em] font-mono-edge">Compare vs</span>
            {availableCompareOptions.map((s) => {
              const isSelected = compareSlider === s;
              const isArchived = archivedSliders.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setCompareSlider(s)}
                  className={`text-[11.5px] px-2.5 py-1 rounded-full border transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:text-foreground hover:border-border/70'
                  }`}
                  data-testid={`fa2-compare-pick-${s}`}
                >
                  {labelForSlider(s)}
                  {!isArchived && <span className="ml-1 text-[9.5px] text-amber-500 uppercase">·new</span>}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3" data-testid="fa2-compare-grid">
          <CompareColumn
            label={`Current — ${labelForSlider(currentSlider)}`}
            tone="current"
            headline={currentHeadline}
            about={currentAbout}
            experienceRewrites={currentExperienceRewrites}
            headlineCharLimit={headlineCharLimit}
            testid="fa2-compare-current"
          />
          {compareSlider && compareVariant ? (
            <CompareColumn
              label={`Compare — ${labelForSlider(compareSlider)}`}
              tone="compare"
              headline={compareVariant.headline || {}}
              about={compareVariant.about || {}}
              experienceRewrites={compareVariant.experienceRewrites || []}
              headlineCharLimit={headlineCharLimit}
              testid="fa2-compare-alt"
            />
          ) : (
            <div
              className="rounded-lg border border-dashed border-primary/30 bg-white/[0.03] p-4 flex flex-col items-start gap-2"
              data-testid="fa2-compare-generate-cta"
            >
              <p className="text-[10.5px] font-mono-edge tracking-[0.16em] uppercase text-muted-foreground">
                {compareSlider ? `${labelForSlider(compareSlider)} not generated yet` : 'Pick a tone above'}
              </p>
              <p className="text-[12.5px] text-muted-foreground">
                {compareSlider
                  ? "You haven't generated this tone for this profile yet. Kick off a regeneration and it'll appear here for comparison."
                  : 'Choose a tone to compare against your current one.'}
              </p>
              {compareSlider && (
                <Button
                  size="sm"
                  className="rounded-full mt-1"
                  disabled={!!regenerating}
                  onClick={() => onRegenerateTone && onRegenerateTone(compareSlider)}
                  data-testid={`fa2-compare-generate-${compareSlider}`}
                >
                  {regenerating ? (
                    <><Loader2 size={12} className="mr-1.5 animate-spin" /> Generating…</>
                  ) : (
                    <><RefreshCw size={12} className="mr-1.5" /> Generate {labelForSlider(compareSlider)}</>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


function CompareColumn({ label, tone, headline, about, experienceRewrites, headlineCharLimit, testid }) {
  const headlineText = (headline?.options?.[headline?.recommendedOption || 0]?.text) || headline?.options?.[0]?.text || '';
  const aboutText = about?.rewrittenCopy || '';
  return (
    <div
      className={`rounded-lg border p-3.5 ${tone === 'current' ? 'border-primary/30 bg-primary/[0.05]' : 'border-emerald-500/25 bg-emerald-500/[0.04]'}`}
      data-testid={testid}
    >
      <p className={`text-[10.5px] font-mono-edge tracking-[0.16em] uppercase mb-2 ${tone === 'current' ? 'text-primary' : 'text-emerald-600 dark:text-emerald-300'}`}>
        {label}
      </p>
      <div className="mb-3">
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">Headline</p>
          {headlineText && <CopyButton text={headlineText} testid={`${testid}-headline-copy`} />}
        </div>
        <p className="text-[13px] leading-snug">{headlineText || <span className="text-muted-foreground italic">— not available —</span>}</p>
        {headlineText && (
          <p className="text-[10.5px] text-muted-foreground mt-0.5">{headlineText.length}/{headlineCharLimit || 220} chars</p>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">About</p>
          {aboutText && <CopyButton text={aboutText} testid={`${testid}-about-copy`} />}
        </div>
        <div className="max-h-72 overflow-y-auto pr-1">
          <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap">
            {aboutText || <span className="text-muted-foreground italic">— not available —</span>}
          </p>
        </div>
      </div>
      {experienceRewrites?.length > 0 && (
        <details className="mt-3 group">
          <summary className="cursor-pointer text-[11px] text-primary select-none hover:underline">
            {experienceRewrites.length} experience rewrite{experienceRewrites.length === 1 ? '' : 's'} →
          </summary>
          <div className="mt-2 space-y-2 max-h-72 overflow-y-auto pr-1">
            {experienceRewrites.slice(0, 5).map((e, i) => (
              <div key={i} className="rounded border border-border p-2 text-[12px]">
                <p className="font-semibold">{e.role}{e.company ? ` — ${e.company}` : ''}</p>
                <p className="mt-1 whitespace-pre-wrap leading-relaxed">{e.rewrittenCopy}</p>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}


function RewriteTab({ audit, toneVariants = {}, onRegenerateTone, regenerating }) {
  const h = audit.headline || {};
  const about = audit.about || {};
  const banner = audit.banner || {};
  const skills = audit.skills || {};

  // Compare Tones state. The current tone is always populated from
  // audit.*; the compare-slot pulls from toneVariants[<slider>] and
  // gracefully offers a "Generate this tone" CTA when the archive is
  // missing that slider.
  const currentSlider = audit.languageSlider;
  const archivedSliders = Object.keys(toneVariants).filter((k) => k !== currentSlider);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareSlider, setCompareSlider] = useState(archivedSliders[0] || null);
  const canCompare = !!currentSlider && !!onRegenerateTone;
  return (
    <div data-testid="fa2-rewrite-tab" className="space-y-1">
      {/* How to use */}
      <div className="rounded-xl border border-primary/30 bg-primary/[0.04] p-3.5 mb-3 flex items-start gap-2.5" data-testid="fa2-rewrite-howto">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15 text-primary shrink-0"><Info size={13} /></span>
        <div className="text-[12.5px] leading-relaxed">
          <p className="font-semibold text-foreground">How to use this section</p>
          <p className="text-muted-foreground">Each block below is a ready-to-paste rewrite for one part of your profile. Copy the vibrant &ldquo;Blayzrr rewrite&rdquo; text and paste it into the matching LinkedIn field (Headline, About, Experience…). Work top-to-bottom — Headline and About together move your score the most. Confirm any amber &ldquo;Please confirm&rdquo; notes before publishing.</p>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="fa2-rewrite-legend">
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-secondary border border-border" /> Current on LinkedIn</span>
        <span className="mx-1">·</span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-primary" /> Blayzrr rewrite (copy-ready)</span>
        {canCompare && (
          <>
            <span className="mx-1">·</span>
            <button
              type="button"
              onClick={() => setCompareOpen((v) => !v)}
              className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-0.5 text-[11px] text-primary hover:bg-primary/10"
              data-testid="fa2-compare-toggle"
            >
              <Layers size={11} />
              {compareOpen ? 'Hide compare' : 'Compare tones'}
            </button>
          </>
        )}
      </div>

      {compareOpen && canCompare && (
        <CompareTonesPanel
          currentSlider={currentSlider}
          currentHeadline={h}
          currentAbout={about}
          currentExperienceRewrites={audit.experienceRewrites || []}
          compareSlider={compareSlider}
          setCompareSlider={setCompareSlider}
          toneVariants={toneVariants}
          archivedSliders={archivedSliders}
          onRegenerateTone={onRegenerateTone}
          regenerating={regenerating}
          headlineCharLimit={audit.headlineCharLimit}
        />
      )}

      {/* Headline */}
      <RewriteSection theme="headline" eyebrow="01 · Headline" title="LinkedIn headline" subtitle="3 options — the recommended one uses your objective's most-scanned keywords." testid="fa2-section-headline">
        {h.current && (
          <CurrentBlock testid="fa2-headline-current">&ldquo;{h.current}&rdquo;</CurrentBlock>
        )}
        <div className="mt-3 space-y-3" data-testid="fa2-headline-options">
          {(h.options || []).map((o, i) => {
            const isRec = i === (h.recommendedOption || 0);
            return (
              <Card key={i} className={isRec ? 'border-primary/70 bg-primary/[0.06] shadow-[0_8px_24px_-14px_rgba(59,130,246,0.35)]' : 'border-border'}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {isRec && <Badge className="bg-primary text-primary-foreground text-[10px]">Blayzrr&rsquo;s Recommended Headline</Badge>}
                    <Badge variant="secondary" className="text-[10px] capitalize">{(o.type || '').replace(/_/g, ' ')}</Badge>
                    <span className="text-[11px] text-muted-foreground">{o.characterCount}/{audit.headlineCharLimit} chars</span>
                    <span className="ml-auto"><CopyButton text={o.text} testid={`fa2-headline-copy-${i}`} /></span>
                  </div>
                  <p className="text-sm font-medium" data-testid={`fa2-headline-text-${i}`}>{o.text}</p>
                  {o.logic && <p className="mt-1.5 text-[12.5px] text-muted-foreground">{o.logic}</p>}
                  <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px] text-muted-foreground">
                    {o.audience && <span>Audience: {o.audience}</span>}
                    {(o.keywords || []).length > 0 && <span>· Keywords: {o.keywords.join(', ')}</span>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </RewriteSection>

      {/* Banner */}
      <RewriteSection theme="banner" eyebrow="02 · Banner" title="Banner recommendation" subtitle="Not observable in a PDF — treat as a design brief for Canva or your designer." testid="fa2-section-banner">
        <Card className="border-violet-500/40 bg-card"><CardContent className="p-4 space-y-1.5 text-[13px]" data-testid="fa2-banner">
          {banner.headline && <p><span className="font-medium text-violet-700 dark:text-violet-300">Headline: </span>{banner.headline}</p>}
          {banner.supportingLine && <p><span className="font-medium text-violet-700 dark:text-violet-300">Supporting line: </span>{banner.supportingLine}</p>}
          {banner.proofLine && <p><span className="font-medium text-violet-700 dark:text-violet-300">Proof line: </span>{banner.proofLine}</p>}
          {banner.cta && <p><span className="font-medium text-violet-700 dark:text-violet-300">CTA: </span>{banner.cta}</p>}
          {banner.visualDirection && <p><span className="font-medium text-violet-700 dark:text-violet-300">Visual direction: </span>{banner.visualDirection}</p>}
          {banner.avoid && <p className="text-muted-foreground"><span className="font-medium">Avoid: </span>{banner.avoid}</p>}
        </CardContent></Card>
      </RewriteSection>

      {/* About */}
      <RewriteSection theme="about" eyebrow="03 · About" title="About section — complete rewrite" subtitle={about.diagnosis || null} testid="fa2-section-about">
        <Card className="border-emerald-500/40 bg-card"><CardContent className="p-4" data-testid="fa2-about-rewrite">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] text-muted-foreground">{about.characterCount}/{audit.aboutCharLimit} chars</span>
            <CopyButton text={about.rewrittenCopy} label="Copy About" testid="fa2-about-copy" />
          </div>
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{about.rewrittenCopy}</p>
          {(about.keywordsUsed || []).length > 0 && (
            <p className="mt-3 text-[11.5px] text-muted-foreground">Keywords: {about.keywordsUsed.join(', ')}</p>
          )}
          {(about.confirmationRequired || []).length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/[0.06] p-2.5 text-[12.5px]" data-testid="fa2-about-confirm">
              <p className="font-medium mb-1">Please confirm before publishing:</p>
              <ul className="list-disc pl-4 space-y-0.5">{about.confirmationRequired.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </div>
          )}
        </CardContent></Card>
      </RewriteSection>

      {/* Experience */}
      <RewriteSection theme="experience" eyebrow="04 · Experience" title="Experience rewrites" subtitle="One rewrite per role — copy directly into LinkedIn." testid="fa2-section-experience">
        <div className="space-y-3" data-testid="fa2-experience-rewrites">
          {(audit.experienceRewrites || []).map((e, i) => (
            <Card key={i} className="border-amber-500/40 bg-card"><CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold text-sm">{e.role}{e.company ? ` — ${e.company}` : ''}</p>
                <Badge variant="secondary" className="text-[10px] capitalize">{e.sourceReliability}</Badge>
                <span className="ml-auto"><CopyButton text={e.rewrittenCopy} testid={`fa2-exp-copy-${i}`} /></span>
              </div>
              {e.diagnosis && <p className="mt-1 text-[12.5px] text-muted-foreground">{e.diagnosis}</p>}
              {(e.context || e.scope) && (
                <p className="mt-1.5 text-[12.5px]"><span className="font-medium">Context/scope: </span>{[e.context, e.scope].filter(Boolean).join(' · ')}</p>
              )}
              <p className="mt-2 text-sm whitespace-pre-wrap leading-relaxed">{e.rewrittenCopy}</p>
              {(e.proofToCollect || []).length > 0 && (
                <p className="mt-2 text-[12.5px] text-amber-700 dark:text-amber-400"><span className="font-medium">Proof to collect: </span>{e.proofToCollect.join('; ')}</p>
              )}
              {e.tenureNote && <p className="mt-1 text-[12.5px] text-muted-foreground">{e.tenureNote}</p>}
              {e.mediaSuggestion && <p className="mt-1 text-[12.5px] text-muted-foreground"><span className="font-medium">Media: </span>{e.mediaSuggestion}</p>}
            </CardContent></Card>
          ))}
        </div>
      </RewriteSection>

      {/* Education & projects */}
      {(audit.educationAndProjects || []).length > 0 && (
        <RewriteSection theme="education" eyebrow="05 · Education" title="Education, projects & certifications" subtitle={null} testid="fa2-section-edu">
          <div className="space-y-2" data-testid="fa2-edu-projects">
            {audit.educationAndProjects.map((x, i) => (
              <Card key={i} className="border-cyan-500/40 bg-card"><CardContent className="p-3.5 text-[13px]">
                <p className="font-semibold">{x.item} <Badge variant="secondary" className="ml-1 text-[10px] capitalize">{x.type}</Badge></p>
                {x.diagnosis && <p className="mt-1 text-muted-foreground">{x.diagnosis}</p>}
                {x.recommendation && <p className="mt-1"><span className="font-medium text-cyan-700 dark:text-cyan-300">Improve: </span>{x.recommendation}</p>}
              </CardContent></Card>
            ))}
          </div>
        </RewriteSection>
      )}

      {/* Skills */}
      <RewriteSection theme="skills" eyebrow="06 · Skills" title="Skills strategy" subtitle="Priority skills lead your endorsement list; remove/rename entries that dilute focus." testid="fa2-section-skills">
        <div className="grid sm:grid-cols-2 gap-3" data-testid="fa2-skills">
          <Card className="border-rose-500/40 bg-card"><CardContent className="p-4">
            <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-rose-700 dark:text-rose-300 mb-2">Priority skills</p>
            <ul className="space-y-1.5 text-[13px]">{(skills.priority || []).map((sk, i) => <li key={i}><span className="font-medium">{sk.skill}</span>{sk.justification ? ` — ${sk.justification}` : ''}</li>)}</ul>
          </CardContent></Card>
          <Card className="border-rose-500/30 bg-card"><CardContent className="p-4">
            <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-rose-700/80 dark:text-rose-300/80 mb-2">Supporting skills</p>
            <ul className="space-y-1.5 text-[13px]">{(skills.supporting || []).map((sk, i) => <li key={i}><span className="font-medium">{sk.skill}</span>{sk.justification ? ` — ${sk.justification}` : ''}</li>)}</ul>
          </CardContent></Card>
          {(skills.removeRenameOrMove || []).length > 0 && (
            <Card className="border-border bg-secondary/40"><CardContent className="p-4">
              <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground mb-2">Remove / rename / move</p>
              <ul className="space-y-1.5 text-[13px]">{skills.removeRenameOrMove.map((sk, i) => <li key={i}><span className="font-medium">{sk.skill}</span> — {(sk.action || '').replace(/_/g, ' ')}{sk.reason ? `: ${sk.reason}` : ''}</li>)}</ul>
            </CardContent></Card>
          )}
          {(skills.proofRecommendations || []).length > 0 && (
            <Card className="border-rose-500/20 bg-card"><CardContent className="p-4">
              <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground mb-2">Needs endorsement / proof</p>
              <ul className="space-y-1.5 text-[13px]">{skills.proofRecommendations.map((sk, i) => <li key={i}>{typeof sk === 'string' ? sk : sk.skill}</li>)}</ul>
            </CardContent></Card>
          )}
        </div>
      </RewriteSection>

      {/* Before & After */}
      {(audit.beforeAfter || []).length > 0 && (
        <RewriteSection theme="ba" eyebrow="07 · Before & After" title="Side-by-side rewrites" subtitle="Muted grey shows what's on your profile today; the indigo tile is Blayzrr's replacement." testid="fa2-section-ba">
          <div className="space-y-3" data-testid="fa2-before-after">
            {audit.beforeAfter.filter((b) => b.before && b.after).map((b, i) => (
              <Card key={i} className="border-indigo-500/40 bg-card"><CardContent className="p-4">
                <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground mb-2">{(b.item || '').replace(/_/g, ' ')}</p>
                <div className="grid sm:grid-cols-2 gap-3 text-[13px]">
                  <div className="rounded-lg border border-dashed border-border bg-secondary/60 p-3">
                    <p className="text-[10px] font-mono-edge tracking-[0.18em] uppercase text-muted-foreground mb-1">Before</p>
                    <p className="whitespace-pre-wrap italic text-muted-foreground">{b.before}</p>
                  </div>
                  <div className="rounded-lg border border-indigo-500/40 bg-indigo-500/[0.06] p-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[10px] font-mono-edge tracking-[0.18em] uppercase text-indigo-700 dark:text-indigo-300">After · Blayzrr rewrite</p>
                      <CopyButton text={b.after} testid={`fa2-ba-copy-${i}`} />
                    </div>
                    <p className="whitespace-pre-wrap">{b.after}</p>
                  </div>
                </div>
              </CardContent></Card>
            ))}
          </div>
        </RewriteSection>
      )}
    </div>
  );
}

// ---------------- Discoverability tab ----------------
export function DiscoverabilityTab({ audit }) {
  const groups = [
    ['primary', 'Primary keyword themes'],
    ['secondary', 'Secondary keyword phrases'],
    ['title_variation', 'Job-title / buyer-language variations'],
  ];
  const km = audit.keywordMap || [];
  return (
    <div data-testid="fa2-discoverability-tab">
      {/* How to use */}
      <div className="rounded-xl border border-sky-500/30 bg-sky-500/[0.05] p-3.5 mb-3 flex items-start gap-2.5" data-testid="fa2-discoverability-howto">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-sky-500/15 text-sky-700 dark:text-sky-300 shrink-0"><Info size={13} /></span>
        <div className="text-[12.5px] leading-relaxed">
          <p className="font-semibold text-foreground">How to use this section</p>
          <p className="text-muted-foreground">These are the words recruiters, hiring managers and buyers actually search when they&rsquo;re looking for someone like you. Weave the primary phrases into your Headline, About and Experience rewrites (Profile Rewrite tab) — one phrase per field, verbatim, without stuffing. Secondary phrases go into role descriptions and skills.</p>
        </div>
      </div>
      <p className="text-[13px] text-muted-foreground max-w-2xl">
        Semantic relevance only — Blayzrr does not claim knowledge of LinkedIn search volumes or ranking probability, and never recommends keyword stuffing.
      </p>
      {groups.map(([type, title]) => {
        const rows = km.filter((k) => k.type === type);
        if (!rows.length) return null;
        return (
          <div key={type}>
            <SectionTitle>{title}</SectionTitle>
            <div className="space-y-2" data-testid={`fa2-keywords-${type}`}>
              {rows.map((k, i) => (
                <Card key={i} className="border-border"><CardContent className="p-3.5 text-[13px]">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold">{k.keyword}</p>
                    {k.status === 'requires_confirmation' && <Badge variant="secondary" className="text-[10px]">Confirm with user</Badge>}
                  </div>
                  {k.relevance && <p className="mt-1"><span className="font-medium">Why: </span>{k.relevance}</p>}
                  {k.evidence && <EvidenceQuote>{k.evidence}</EvidenceQuote>}
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {k.targetSearcher && <span className="text-[11.5px] text-muted-foreground">Searcher: {k.targetSearcher}</span>}
                    {(k.placement || []).map((p) => (
                      <Badge key={p} variant="secondary" className="text-[10px]">{p.replace(/_/g, ' ')}</Badge>
                    ))}
                  </div>
                </CardContent></Card>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------- Proof & Credibility tab ----------------
export function ProofTab({ audit }) {
  const c = audit.credibilityAudit || {};
  const group = (title, rows, render, testid) => (
    (rows || []).length > 0 && (
      <div key={title}>
        <SectionTitle>{title}</SectionTitle>
        <div className="space-y-2" data-testid={testid}>{rows.map(render)}</div>
      </div>
    )
  );
  return (
    <div data-testid="fa2-proof-tab">
      {/* How to use */}
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.05] p-3.5 mb-3 flex items-start gap-2.5" data-testid="fa2-proof-howto">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 shrink-0"><Info size={13} /></span>
        <div className="text-[12.5px] leading-relaxed">
          <p className="font-semibold text-foreground">How to use this section</p>
          <p className="text-muted-foreground">Green rows are claims your profile <em>already backs up</em> — safe to keep. Amber rows need a number, artefact or link to earn trust. Red rows are risky — either add proof or soften the wording. Answer the &ldquo;questions to strengthen your profile&rdquo; and load results into the Featured section to convert readers into replies.</p>
        </div>
      </div>
      {group('Supported claims', c.supportedClaims, (r, i) => (
        <Card key={i} className="border-emerald-500/30"><CardContent className="p-3.5 text-[13px]">
          <p className="font-medium">{r.claim}</p><EvidenceQuote>{r.evidence}</EvidenceQuote>
        </CardContent></Card>
      ), 'fa2-supported-claims')}
      {group('Partially supported claims', c.partialClaims, (r, i) => (
        <Card key={i} className="border-amber-500/30"><CardContent className="p-3.5 text-[13px]">
          <p className="font-medium">{r.claim}</p><EvidenceQuote>{r.evidence}</EvidenceQuote>
          {r.gap && <p className="mt-1 text-muted-foreground">Gap: {r.gap}</p>}
        </CardContent></Card>
      ), 'fa2-partial-claims')}
      {group('Unsupported claims', c.unsupportedClaims, (r, i) => (
        <Card key={i} className="border-destructive/30"><CardContent className="p-3.5 text-[13px]">
          <p className="font-medium">{r.claim}</p>{r.issue && <p className="mt-1 text-muted-foreground">{r.issue}</p>}
        </CardContent></Card>
      ), 'fa2-unsupported-claims')}
      {(c.proofOpportunities || []).length > 0 && (
        <>
          <SectionTitle>Missing proof opportunities</SectionTitle>
          <ul className="list-disc pl-5 space-y-1 text-[13px]" data-testid="fa2-proof-opportunities">
            {c.proofOpportunities.map((p, i) => <li key={i}>{typeof p === 'string' ? p : JSON.stringify(p)}</li>)}
          </ul>
        </>
      )}
      {(c.questionsForUser || []).length > 0 && (
        <>
          <SectionTitle>Answer these to strengthen your profile</SectionTitle>
          <ol className="list-decimal pl-5 space-y-1.5 text-[13px]" data-testid="fa2-proof-questions">
            {c.questionsForUser.map((q, i) => <li key={i}>{q}</li>)}
          </ol>
        </>
      )}

      {(audit.featuredRecommendations || []).length > 0 && (
        <>
          <SectionTitle>Featured-section strategy</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="fa2-featured">
            {audit.featuredRecommendations.map((f, i) => (
              <Card key={i} className="border-border"><CardContent className="p-4 text-[13px] space-y-1">
                <p className="font-semibold">{f.title}</p>
                {f.purpose && <p><span className="font-medium">Purpose: </span>{f.purpose}</p>}
                {f.audience && <p><span className="font-medium">Audience: </span>{f.audience}</p>}
                {f.cta && <p><span className="font-medium">CTA: </span>{f.cta}</p>}
                <Badge variant="secondary" className="text-[10px]">{f.hasSourceMaterial ? 'You likely have source material' : 'Needs new material'}</Badge>
              </CardContent></Card>
            ))}
          </div>
        </>
      )}

      <SectionTitle>Manual LinkedIn checks</SectionTitle>
      <p className="text-[12.5px] text-muted-foreground mb-2 max-w-2xl">
        These are not reliably visible in a LinkedIn PDF, so they are never scored — review them directly on LinkedIn.
      </p>
      <div className="grid sm:grid-cols-2 gap-2" data-testid="fa2-manual-checks">
        {(audit.manualChecks || []).map((m, i) => (
          <div key={i} className="flex items-start gap-2 rounded-lg border border-border bg-card px-3 py-2 text-[12.5px]">
            <ShieldCheck size={13} className="mt-0.5 text-muted-foreground shrink-0" />
            <div><p className="font-medium">{m.item}</p><p className="text-muted-foreground text-[11.5px]">{m.status}</p></div>
          </div>
        ))}
      </div>

      {Array.isArray(audit.trustLayerChecklist) && audit.trustLayerChecklist.length > 0 && (
        <>
          <SectionTitle>Trust-layer checklist</SectionTitle>
          <p className="text-[12.5px] text-muted-foreground mb-2 max-w-2xl">
            The 6 credibility levers that make a profile land — recruiters, buyers and peers scan for these before anything else.
          </p>
          <div className="grid sm:grid-cols-2 gap-2" data-testid="fa2-trust-checklist">
            {audit.trustLayerChecklist.map((c, i) => {
              const s = String(c.status || '').toLowerCase();
              const isPass = s === 'pass';
              const isFail = s === 'fail';
              const isPartial = s === 'partial';
              const badgeCls = isPass
                ? 'border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-700 dark:text-emerald-300'
                : isFail
                  ? 'border-red-500/40 bg-red-500/[0.06] text-red-700 dark:text-red-300'
                  : isPartial
                    ? 'border-amber-500/40 bg-amber-500/[0.06] text-amber-700 dark:text-amber-300'
                    : 'border-border bg-muted/40 text-muted-foreground';
              const labelText = isPass ? 'PASS' : isFail ? 'FAIL' : isPartial ? 'PARTIAL' : 'MANUAL';
              return (
                <div
                  key={i}
                  className="flex items-start gap-2 rounded-lg border border-border bg-card px-3 py-2 text-[12.5px]"
                  data-testid={`fa2-trust-item-${i}`}
                >
                  <span className={`shrink-0 text-[10px] font-mono-edge tracking-[0.16em] px-2 py-0.5 rounded-full border ${badgeCls}`}>
                    {labelText}
                  </span>
                  <div className="min-w-0">
                    <p className="font-medium">{c.item}</p>
                    {c.note && <p className="text-muted-foreground text-[11.5px] leading-snug">{c.note}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------- Presence plan tab ----------------
export function PresenceTab({ audit, onDraftFromTheme, onDraftPovFromTheme }) {
  return (
    <div data-testid="fa2-presence-tab">
      <p className="text-[10.5px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">Grow presence</p>
      <h2 className="mt-1 text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-50">
        Themes worth showing up around
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-slate-600 dark:text-slate-300 max-w-[68ch]">
        Goal-specific presence building — no algorithm tricks, no reach guarantees.
      </p>
      <div className="mt-5 space-y-3">
        {(audit.presencePlan || []).map((p, i) => (
          <Card
            key={i}
            className="border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 rounded-2xl shadow-sm"
            data-testid={`fa2-presence-theme-${i}`}
          >
            <CardContent className="p-5 sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold text-slate-900 dark:text-slate-50 leading-snug">{p.theme}</p>
                  {p.why && (
                    <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600 dark:text-slate-300 max-w-[68ch]">
                      {p.why}
                    </p>
                  )}
                </div>
                {(onDraftFromTheme || onDraftPovFromTheme) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="rounded-full shrink-0"
                        data-testid={`fa2-presence-draft-${i}`}
                      >
                        <Sparkles size={12} className="mr-1.5" /> Draft a post <ChevronDown size={11} className="ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      {onDraftFromTheme && (
                        <DropdownMenuItem
                          onSelect={(e) => { e.preventDefault(); onDraftFromTheme(p); }}
                          data-testid={`fa2-presence-draft-prompt-${i}`}
                        >
                          <Sparkles size={12} className="mr-2 text-primary" /> Prompt Post
                        </DropdownMenuItem>
                      )}
                      {onDraftPovFromTheme && (
                        <DropdownMenuItem
                          onSelect={(e) => { e.preventDefault(); onDraftPovFromTheme(p); }}
                          data-testid={`fa2-presence-draft-pov-${i}`}
                        >
                          <MessageSquareQuote size={12} className="mr-2 text-[hsl(var(--accent))]" /> POV Post
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              {(p.actions || []).length > 0 && (
                <ul className="mt-3.5 space-y-1.5">
                  {p.actions.map((a, j) => (
                    <li key={j} className="flex items-start gap-2 text-[14px] leading-relaxed text-slate-700 dark:text-slate-200">
                      <span className="mt-2 shrink-0 h-1 w-1 rounded-full bg-primary/70" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------------- 30-day plan tab ----------------
const OWNER_LABELS = {
  blayzrr_automatic: 'Blayzrr can do this automatically',
  user_input: 'Needs your input',
  inside_linkedin: 'Do this inside LinkedIn',
};

export function PlanTab({ audit, phaseKeys }) {
  const plan = audit.thirtyDayPlan || {};
  const allPhases = [
    ['today', 'Today'], ['next7Days', 'Next 7 days'],
    ['next14Days', 'Next 14 days'], ['next30Days', 'Next 30 days'],
  ];
  const phases = phaseKeys ? allPhases.filter(([k]) => phaseKeys.includes(k)) : allPhases;
  return (
    <div data-testid="fa2-plan-tab" className="space-y-8">
      {phases.map(([key, title]) => (
        <div key={key}>
          <p className="text-[10.5px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">Timeline</p>
          <h2 className="mt-1 text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-50">{title}</h2>
          <Card className="mt-3 border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 rounded-2xl shadow-sm">
            <CardContent className="p-4 sm:p-5">
              <div className="space-y-2.5" data-testid={`fa2-plan-${key}`}>
                {(plan[key] || []).map((a, i) => (
                  <div
                    key={i}
                    className="flex flex-wrap items-start gap-2.5 rounded-xl border border-slate-200/70 dark:border-slate-800 px-4 py-3 text-[14px] leading-relaxed"
                  >
                    <Pill value={a.impact} />
                    <p className="flex-1 min-w-[200px] text-slate-800 dark:text-slate-100">{a.action}</p>
                    {a.owner && (
                      <Badge variant="secondary" className="text-[11px]">
                        {OWNER_LABELS[a.owner] || a.owner}
                      </Badge>
                    )}
                  </div>
                ))}
                {(plan[key] || []).length === 0 && (
                  <p className="text-[13px] text-muted-foreground italic">No actions for this window.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      ))}
    </div>
  );
}

// 7-Day view — only ``today`` + ``next7Days``. Used by the "Grow
// Presence · 7-Day Plan" nav slot in the v2 layout so users get a
// focused, do-this-week list separate from the full 30-day plan.
export function SevenDayPlanTab({ audit }) {
  return <PlanTab audit={audit} phaseKeys={['today', 'next7Days']} />;
}

// ---------------- Root ----------------
/**
 * Embeddable body — used inside AnalysisPage's ResultsTabs as the
 * "Audit Report" tab so the post-creation tabs (Researched / POV /
 * Prompt Posts, Inspiration) stay available next to the v2 audit.
 */
export function FullAuditBody({ data }) {
  // Keep the audit in local state so a tone-regenerate refreshes the
  // view in place without a full page reload / round-trip re-fetch.
  const [audit, setAudit] = useState(data.full_audit_v2 || {});
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratingDimKey, setRegeneratingDimKey] = useState(null);
  // Controlled tabs so the "Improve my profile" CTAs on the Overview
  // tab (summary card, Fix These First cards, sticky bar) can jump the
  // user directly to the Profile Rewrite tab in-page.
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();
  const handleGoToRewrite = () => {
    setActiveTab('rewrite');
    // Scroll to the top of the tab content so the user lands on
    // Headline (the first block) rather than mid-scroll.
    setTimeout(() => {
      try {
        document.querySelector('[data-testid="fa2-tab-rewrite"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch { /* noop */ }
    }, 30);
  };
  const handleChangeGoal = () => {
    navigate('/dashboard');
  };

  const handleRegenerateDimension = async (dimensionKey) => {
    if (!data?.id || !dimensionKey || regeneratingDimKey) return;
    setRegeneratingDimKey(dimensionKey);
    const toastId = toast.loading('Rewriting this dimension… ~15-30s.');
    try {
      const { linkedge } = await import('../../lib/api');
      const res = await linkedge.regenerateDimension(data.id, dimensionKey);
      if (res?.dimension) {
        setAudit((prev) => ({
          ...prev,
          dimensions: (prev.dimensions || []).map((d) =>
            d.key === dimensionKey ? { ...d, ...res.dimension } : d
          ),
        }));
        toast.success('Dimension rewritten.', { id: toastId });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || 'Regenerate failed') : (detail || 'Regenerate failed');
      toast.error(msg, { id: toastId });
    } finally {
      setRegeneratingDimKey(null);
    }
  };

  const handleRegenerateTone = async (slider) => {
    if (regenerating) return;
    if (!data?.id) {
      toast.error('This audit is missing an id — try refreshing the page.');
      return;
    }
    if (!slider || slider === audit.languageSlider) return;
    setRegenerating(true);
    const toastId = toast.loading('Re-writing your profile in the new tone… this can take 2-4 minutes.');
    try {
      const { linkedge } = await import('../../lib/api');
      // 202 fire-and-poll pattern — the LLM run is longer than the CF
      // edge timeout, so we kick off a background job and poll status.
      await linkedge.regenerateTone(data.id, slider);
      const started = Date.now();
      const maxMs = 6 * 60 * 1000;
      let done = false;
      let lastState = 'running';
      while (Date.now() - started < maxMs) {
        await new Promise((r) => setTimeout(r, 6000));
        const status = await linkedge.regenerateToneStatus(data.id);
        lastState = status?.state;
        if (status?.state === 'done' && status?.full_audit_v2?.languageSlider === slider) {
          setAudit(status.full_audit_v2);
          toast.success(`Rewrite tone changed to ${labelForSlider(slider)}.`, { id: toastId });
          done = true;
          break;
        }
        if (status?.state === 'failed') {
          toast.error(status?.error || 'Regenerate failed. Please try again.', { id: toastId });
          done = true;
          break;
        }
      }
      if (!done) {
        toast.error(`Still running (${lastState}) — refresh in a minute to see the new tone.`, { id: toastId });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || 'Regenerate failed') : (detail || 'Regenerate failed');
      toast.error(msg, { id: toastId });
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div data-testid="full-audit-report">
      <p className="text-sm text-muted-foreground">
        {audit.auditName || 'LinkedIn Profile and Positioning Audit'} · Based on your uploaded LinkedIn PDF
      </p>
      <p className="mt-1.5 text-[11.5px] text-muted-foreground max-w-3xl" data-testid="fa2-disclaimer">
        {audit.disclaimer}
      </p>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-5">
        <TabsList className="bg-secondary rounded-xl p-1 flex-wrap h-auto" data-testid="fa2-tabs">
          <TabsTrigger value="overview" className="rounded-lg" data-testid="fa2-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="rewrite" className="rounded-lg" data-testid="fa2-tab-rewrite">Profile Rewrite</TabsTrigger>
          <TabsTrigger value="discoverability" className="rounded-lg" data-testid="fa2-tab-discoverability">Discoverability</TabsTrigger>
          <TabsTrigger value="proof" className="rounded-lg" data-testid="fa2-tab-proof">Proof and Credibility</TabsTrigger>
          <TabsTrigger value="presence" className="rounded-lg" data-testid="fa2-tab-presence">LinkedIn Presence Plan</TabsTrigger>
          <TabsTrigger value="plan" className="rounded-lg" data-testid="fa2-tab-plan">30-Day Action Plan</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-5">
          <OverviewTab
            audit={audit}
            rawData={data}
            onRegenerateTone={handleRegenerateTone}
            regenerating={regenerating}
            onRegenerateDimension={handleRegenerateDimension}
            regeneratingDimKey={regeneratingDimKey}
            onGoToRewrite={handleGoToRewrite}
            onChangeGoal={handleChangeGoal}
          />
        </TabsContent>
        <TabsContent value="rewrite" className="mt-5"><RewriteTab audit={audit} toneVariants={data.tone_variants || {}} onRegenerateTone={handleRegenerateTone} regenerating={regenerating} /></TabsContent>
        <TabsContent value="discoverability" className="mt-5"><DiscoverabilityTab audit={audit} /></TabsContent>
        <TabsContent value="proof" className="mt-5"><ProofTab audit={audit} /></TabsContent>
        <TabsContent value="presence" className="mt-5"><PresenceTab audit={audit} /></TabsContent>
        <TabsContent value="plan" className="mt-5"><PlanTab audit={audit} /></TabsContent>
      </Tabs>
    </div>
  );
}

export default function FullAuditReport({ data }) {
  const navigate = useNavigate();
  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="fa2-back-button">
          <ArrowLeft size={16} className="mr-2" /> Dashboard
        </Button>
        <h1 className="mt-2 text-3xl font-bold" data-testid="fa2-title">{data.profile_name || 'Your audit'}</h1>
        <div className="mt-2">
          <FullAuditBody data={data} />
        </div>
      </div>
    </AppShell>
  );
}
